<template>
  <v-card class="py-6 d-flex flex-column" rounded="lg" elevation="0">
    <div class="px-6 d-flex align-center justify-space-between mb-4">
      <div class="subtitle2">{{ $t("network.overview.map.title") }}</div>
      <div class="d-flex align-center flex-wrap map-legend">
        <div v-for="entry in legend" :key="entry.type" class="d-flex align-center ml-6">
          <span class="legend-dot mr-2" :style="{ backgroundColor: entry.color }"></span>
          <span class="caption text-indigo-medium">{{ entry.label }}</span>
        </div>
      </div>
    </div>

    <div v-if="loading" class="px-6 flex-grow-1">
      <v-skeleton-loader type="image" height="100%" />
    </div>

    <div v-else class="px-6 flex-grow-1 d-flex">
      <div class="map-viewport flex-grow-1">
        <dotted-world-map
          :nodes="mapNodes"
          :arcs="mapArcs"
          fit="cover"
          snap-nodes
          cluster-nodes
          :ariaLabel="$t('network.overview.map.title')"
          :zoom-in-label="$t('network.overview.map.zoomIn')"
          :zoom-out-label="$t('network.overview.map.zoomOut')"
        >
          <template #tooltip="{ nodes }">
            <div class="map-tooltip">
              <template v-if="nodes.length === 1">
                <div class="subtitle2">{{ tooltipFor(nodes[0]).label }}</div>
                <div class="caption text-indigo-medium">
                  {{ $t(`network.overview.type.${tooltipFor(nodes[0]).connectionType}`) }} ·
                  {{ $t("network.overview.map.connections", { n: tooltipFor(nodes[0]).count }) }}
                </div>
              </template>

              <!-- Several places drawn as one marker. The header answers "how many and how much" at a
                   glance, the list names them, and the hint says how to take them apart. -->
              <template v-else>
                <div class="subtitle2">
                  {{ $t("network.overview.map.locations", { n: nodes.length }) }}
                </div>
                <div class="caption text-indigo-medium mb-2">
                  {{ $t(`network.overview.type.${tooltipFor(nodes[0]).connectionType}`) }} ·
                  {{ $t("network.overview.map.connections", { n: totalCount(nodes) }) }}
                </div>
                <div
                  v-for="entry in clusterList(nodes)"
                  :key="entry.id"
                  class="caption d-flex justify-space-between cluster-row"
                >
                  <span class="mr-4">{{ entry.label }}</span>
                  <span class="text-indigo-medium">{{ entry.count }}</span>
                </div>
                <div v-if="nodes.length > CLUSTER_LIST_LIMIT" class="caption text-indigo-medium">
                  {{ $t("network.overview.map.andMore", { n: nodes.length - CLUSTER_LIST_LIMIT }) }}
                </div>
                <div class="caption text-indigo-medium mt-2">
                  {{ $t("network.overview.map.zoomToSeparate") }}
                </div>
              </template>
            </div>
          </template>
        </dotted-world-map>
      </div>
    </div>

    <div v-if="!loading && unlocatedCount > 0" class="px-6 caption text-indigo-medium mt-2">
      {{ $t("network.overview.map.unlocated", { n: unlocatedCount }) }}
    </div>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { useTheme } from "vuetify";
import DottedWorldMap from "@/components/dotted-world-map/DottedWorldMap.vue";
import { LAT_NORTH, LAT_SOUTH } from "@/components/dotted-world-map/world-land";
import { NetworkConnectionType, NetworkMapNodeRole } from "@/constants/network";
import type { DottedMapNode } from "@/components/dotted-world-map/types";
import type {
  NetworkAnalyticsMap,
  NetworkMapNode,
} from "@/_store/network/network-analytics.module";

/** Everything the tooltip needs, carried alongside the geometry the map itself cares about. */
interface ConnectionMapNode extends DottedMapNode {
  label: string;
  connectionType: NetworkConnectionType;
  count: number;
}

/** Longitude wrapped into the -180..180 the projection draws, so a 0..360 record still lands on the map. */
const normaliseLongitude = (longitude: number) => ((((longitude + 180) % 360) + 360) % 360) - 180;

export default defineComponent({
  components: { DottedWorldMap },
  props: {
    data: {
      type: Object as PropType<NetworkAnalyticsMap | null>,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const theme = useTheme();

    const colors = computed<Record<NetworkConnectionType, string>>(() => ({
      [NetworkConnectionType.VIRTUAL_OFFICE]: theme.current.value.colors["orange-light"],
      [NetworkConnectionType.SITE_TO_SITE]: theme.current.value.colors["primary"],
      [NetworkConnectionType.USER]: theme.current.value.colors["blue-light"],
    }));

    const legend = computed(() =>
      [
        NetworkConnectionType.VIRTUAL_OFFICE,
        NetworkConnectionType.SITE_TO_SITE,
        NetworkConnectionType.USER,
      ].map((type) => ({
        type,
        label: i18n.t(`network.overview.type.${type}`),
        color: colors.value[type],
      }))
    );

    /** Nodes the map can actually place: geolocated, and inside the latitude band it draws. */
    const plottable = computed(() =>
      (props.data?.nodes ?? []).filter(
        (node) =>
          Number.isFinite(node.latitude) &&
          Number.isFinite(node.longitude) &&
          (node.latitude as number) <= LAT_NORTH &&
          (node.latitude as number) >= LAT_SOUTH
      )
    );

    /**
     * Draw order, and the reason it matters.
     *
     * Locations overlap: a city that hosts a Virtual Office server usually has clients in it too, and at
     * low zoom their dots land on the same pixels. Whichever is drawn last wins, so without an explicit
     * order a blue client dot sitting on an orange server's halo reads as "blue dot with an orange rim" —
     * a colour that appears nowhere in the legend and means nothing. Servers are therefore drawn last,
     * over their clients, and every dot gets a ring in the map's background colour so that the one on top
     * is unambiguous rather than blended.
     */
    const DRAW_ORDER: Record<NetworkConnectionType, number> = {
      [NetworkConnectionType.USER]: 0,
      [NetworkConnectionType.SITE_TO_SITE]: 1,
      [NetworkConnectionType.VIRTUAL_OFFICE]: 2,
    };

    const mapNodes = computed<ConnectionMapNode[]>(() => {
      const nodes = plottable.value;
      // Reduced rather than spread into Math.max: a spread argument list is bounded by the call
      // stack, and blowing it here would throw during render and unmount the whole card.
      const maxCount = nodes.reduce((max, node) => Math.max(max, node.count ?? 1), 1);

      return nodes
        .map((node: NetworkMapNode) => {
          // Area, not radius, tracks the count so a busy location does not swamp the map.
          const relative = Math.sqrt((node.count ?? 1) / maxCount);
          const diameter = 8 + relative * 10;
          // Straight from the legend, by connection type. A node's colour says what kind of connection
          // it is; nothing else is allowed to tint it.
          const color = colors.value[node.type] ?? colors.value[NetworkConnectionType.USER];
          const isHub = node.role === NetworkMapNodeRole.SERVER;

          return {
            id: node.id,
            latitude: node.latitude as number,
            // Wrapped rather than dropped: a record on the 0..360 convention is a real place, and left
            // as-is it projects hundreds of pixels off the right edge while still counting as plotted.
            longitude: normaliseLongitude(node.longitude as number),
            // Only ever merge like with like: a Virtual Office server and the clients around it are
            // different things, and one marker cannot honestly be both colours at once.
            clusterGroup: node.type,
            color,
            diameter,
            // Separates the dot from anything it overlaps, so the topmost colour stays readable.
            ringDiameter: diameter + 4,
            // Hubs carry a soft halo in their own colour, so they read as hubs without changing hue.
            haloDiameter: isHub ? diameter + 14 : 0,
            haloColor: color,
            label: node.label ?? node.city ?? node.id,
            connectionType: node.type,
            count: node.count ?? 0,
          };
        })
        .sort((a, b) => (DRAW_ORDER[a.connectionType] ?? 0) - (DRAW_ORDER[b.connectionType] ?? 0));
    });

    const mapArcs = computed(() =>
      (props.data?.arcs ?? []).map((arc) => ({
        fromNodeId: arc.fromNodeId,
        toNodeId: arc.toNodeId,
      }))
    );

    /**
     * Counts what the map does not show, which is more than what the backend could not geolocate: a
     * node outside the drawn latitude band has coordinates but no place on the map, and would
     * otherwise disappear with no explanation at all.
     */
    const unlocatedCount = computed(() => {
      const plotted = new Set(plottable.value.map((node) => node.id));
      // Connections, not nodes: the caption reads "{n} connections could not be placed", and a single
      // node stands for however many connections its `count` reports.
      const excluded = (props.data?.nodes ?? [])
        .filter((node) => Number.isFinite(node.latitude) && Number.isFinite(node.longitude))
        .filter((node) => !plotted.has(node.id))
        .reduce((total, node) => total + (node.count ?? 0), 0);

      return (props.data?.meta?.unlocatedCount ?? 0) + excluded;
    });

    const tooltipFor = (node: DottedMapNode) => node as ConnectionMapNode;

    /** Long enough to recognise the place, short enough that the tooltip stays a tooltip. */
    const CLUSTER_LIST_LIMIT = 5;

    const totalCount = (nodes: DottedMapNode[]) =>
      nodes.reduce((sum, node) => sum + tooltipFor(node).count, 0);

    /**
     * Busiest first, and by name between places that are equally busy.
     *
     * Connections are what the reader is looking for, and they also decide which places survive the
     * truncation. The name is the tie-break rather than an afterthought: without it the order among
     * equal counts would be whatever order the backend happened to return, and a cluster would
     * reshuffle itself between refreshes for no reason the reader could see.
     */
    const clusterList = (nodes: DottedMapNode[]) =>
      [...nodes]
        .map(tooltipFor)
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
        .slice(0, CLUSTER_LIST_LIMIT);

    return {
      legend,
      mapNodes,
      mapArcs,
      unlocatedCount,
      tooltipFor,
      totalCount,
      clusterList,
      CLUSTER_LIST_LIMIT,
    };
  },
});
</script>

<style scoped lang="scss">
.map-viewport {
  position: relative;
  // Only sets the height where nothing else does — stacked on a phone, where the card takes its
  // natural size. On desktop the row stretches this to match the column beside it, and the cover fit
  // hands that height to the map rather than padding it out with sea.
  aspect-ratio: 360 / 134;
  min-height: 150px;
  min-width: 0;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.map-tooltip {
  background: rgb(var(--v-theme-white));
  border: 1px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 8px;
  padding: 6px 10px;
  white-space: nowrap;
  // A cluster lists its places, and the busiest of them can carry a long city name. Capped so the
  // tooltip cannot grow wider than the card it floats over.
  max-width: 260px;
}

.cluster-row {
  gap: 8px;

  span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
